/**
 * Композиционный корень веб-клиента.
 *
 * Здесь и только здесь создаются конкретные реализации портов. Экраны и
 * функции получают их готовыми и не знают про IndexedDB и браузерные API
 * (docs/ARCHITECTURE.md §2).
 */

import type { InstallationId } from '@type2x/shared-contracts';
import type { Clock, IdFactory, WorkspaceRepository } from '@type2x/feature-personal-workspace';
import {
  ApiClient,
  createBrowserClock,
  createJsonSaver,
  createIdFactory,
  createInMemorySyncQueueRepository,
  createInMemoryWorkspaceRepository,
  createIndexedDbSyncQueueRepository,
  createIndexedDbWorkspaceRepository,
  resolveInstallationId,
} from '@type2x/adapter-web';
import type { AccountsGateway } from '@type2x/feature-accounts';
import type { JsonSaver } from '@type2x/adapter-web';
import type { SyncGateway, SyncQueueRepository } from '@type2x/feature-sync';
import type { ControlTestGateway } from '@type2x/feature-control-test';
import type { FriendsGateway, LeaderboardGateway } from '@type2x/feature-leaderboard';

import {
  createAccountsGateway,
  createControlTestGateway,
  createFriendsGateway,
  createLeaderboardGateway,
  createSyncGateway,
} from './gateway.js';

/** Набор зависимостей приложения. */
export interface Runtime {
  readonly clock: Clock;
  readonly ids: IdFactory;
  readonly repository: WorkspaceRepository;
  readonly installationId: InstallationId;
  readonly accounts: AccountsGateway;
  readonly sync: SyncGateway;
  readonly queueRepository: SyncQueueRepository;
  readonly controlTests: ControlTestGateway;
  /** Таблицы лидеров: чтение снимка и управление участием (ТЗ FR-LB-06). */
  readonly leaderboard: LeaderboardGateway;
  /** Дружеский рейтинг: связи по коду приглашения (ТЗ FR-LB-01). */
  readonly friends: FriendsGateway;
  /**
   * Код приглашения, с которым открыли приложение; `null` — обычный заход.
   *
   * Сам по себе он ничего не делает: приглашение показывается и ждёт
   * согласия. Ссылка, молча добавляющая человека в друзья, — это уже не
   * приглашение (ТЗ FR-GM-09).
   */
  readonly friendInvite: string | null;
  /** Сохранение выгрузки прогресса файлом. */
  readonly files: JsonSaver;
  /** Ссылка из письма, если приложение открыто по ней. */
  readonly emailLink: EmailLink | null;
}

/** Ссылка из письма: зачем пришли и с каким токеном. */
export interface EmailLink {
  readonly kind: 'verify-email' | 'reset-password';
  readonly token: string;
}

/** Имя параметра токена. Совпадает с тем, что ставит сервер. */
const TOKEN_PARAM = 'token';

/** Пути писем. Токен в обоих один и тот же параметр, различается назначение. */
const LINK_KIND_BY_PATH: Record<string, EmailLink['kind']> = {
  '/verify-email': 'verify-email',
  '/reset-password': 'reset-password',
};

/**
 * Забирает токен из адресной строки и убирает его оттуда.
 *
 * Токен — ключ: оставленный в адресе, он попадёт в историю браузера и в
 * заголовок Referer, а при перезагрузке страницы будет предъявлен второй раз.
 * Поэтому он читается один раз при запуске и сразу стирается из адреса.
 *
 * Назначение определяет путь, а не параметр: подтверждение адреса и смена
 * пароля — разные действия, и перепутать их нельзя.
 */
function takeEmailLink(): EmailLink | null {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  const kind = LINK_KIND_BY_PATH[url.pathname];
  const token = url.searchParams.get(TOKEN_PARAM);
  if (kind === undefined || token === null || token === '') return null;

  url.searchParams.delete(TOKEN_PARAM);
  window.history.replaceState(window.history.state, '', url.pathname + url.search);
  return { kind, token };
}

/** Имя части адреса, в которой приходит приглашение в друзья. */
const FRIEND_FRAGMENT = 'friend=';

/**
 * Забирает код приглашения из адреса и убирает его оттуда.
 *
 * Код приходит после решётки, а не параметром запроса: часть адреса после неё
 * браузер серверу не отправляет вовсе. Значит, код не попадёт ни в наши
 * журналы обращений, ни в заголовок `Referer` на чужих страницах — а
 * найденный там код приводит к человеку того, кого он не звал.
 *
 * Из адресной строки код стирается сразу, как и токен из письма: иначе он
 * останется в истории браузера и будет предъявлен снова при перезагрузке.
 */
function takeFriendInvite(): string | null {
  if (typeof window === 'undefined') return null;

  const fragment = window.location.hash.replace(/^#/u, '');
  if (!fragment.startsWith(FRIEND_FRAGMENT)) return null;

  const code = decodeURIComponent(fragment.slice(FRIEND_FRAGMENT.length));
  if (code === '') return null;

  const url = new URL(window.location.href);
  window.history.replaceState(window.history.state, '', url.pathname + url.search);
  return code;
}

/**
 * Собирает зависимости.
 *
 * Если IndexedDB недоступна (приватный режим части браузеров), используется
 * хранилище в памяти: тренировка обязана работать, потеряв только историю.
 */
export function createRuntime(): Runtime {
  const clock = createBrowserClock();
  const ids = createIdFactory(() => clock.epochMs());

  let repository: WorkspaceRepository;
  let queueRepository: SyncQueueRepository;
  try {
    repository = createIndexedDbWorkspaceRepository();
    queueRepository = createIndexedDbSyncQueueRepository();
  } catch {
    repository = createInMemoryWorkspaceRepository();
    queueRepository = createInMemorySyncQueueRepository();
  }

  const client = new ApiClient({ baseUrl: import.meta.env['VITE_API_BASE_URL'] ?? '' });

  return {
    clock,
    ids,
    repository,
    installationId: resolveInstallationId(ids),
    accounts: createAccountsGateway(client),
    sync: createSyncGateway(client),
    queueRepository,
    controlTests: createControlTestGateway(client),
    leaderboard: createLeaderboardGateway(client),
    friends: createFriendsGateway(client),
    files: createJsonSaver(),
    emailLink: takeEmailLink(),
    friendInvite: takeFriendInvite(),
  };
}
